import { ActionIcon, Tooltip } from '@mantine/core';
import { IconDownload, IconEye } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './AttachmentFileActions.module.css';
import { useAttachmentFiles } from './AttachmentFiles.js';
import { AttachmentPreview } from './AttachmentPreview.js';
import { canPreview } from './preview-core.js';

export interface AttachmentFileActionsProps {
  /** Ekin kimliği; adres verilmezse uygulamanın ek erişiminden (`AttachmentFilesContext`). */
  id: string;
  fileName: string;
  /** Listedeki tür ipucu: "göz" yalnız önizlenebilir türde. Açılışta sunucunun türü esastır. */
  mimeType: string;
  /** Ekranın verdiği dosya adresi (web ofis). `null`: bu dosyanın ek ucu yok, simge yok. */
  href?: string | null | undefined;
}

function errorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : 'SAVE_FAILED';
}

/**
 * Ekin "göz" (önizleme) ve indirme simgeleri (ortak 2026-10-24 madde 4; ADR 0126). Gemi
 * uygulamasında ve web ofiste aynı bileşen: adres ekranın kaynağından ya da uygulamanın ek
 * erişiminden gelir; ikisi de yoksa simge çizilmez. Simgenin adı dosya adını taşır
 * ("Preview <dosya adı>"); önizleme penceresi Esc ile kapanır.
 */
export function AttachmentFileActions({
  id,
  fileName,
  mimeType,
  href,
}: AttachmentFileActionsProps) {
  const { t } = useTranslation();
  const files = useAttachmentFiles();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = href === null ? undefined : (href ?? files?.url(id));
  if (url === undefined) return null;
  // Gemide indirme kaydetme penceresiyle; adres ekrandan geldiyse bağlantı indirir
  const save = href === undefined ? files?.save : undefined;
  const download = save
    ? () => {
        setError(null);
        save({ id, fileName }).catch((cause: unknown) => setError(errorCode(cause)));
      }
    : undefined;
  const previewLabel = t('attachmentPreview.preview', { name: fileName });
  const downloadLabel = t('attachmentPreview.download', { name: fileName });
  return (
    <span className={classes.actions}>
      {canPreview(mimeType) ? (
        <Tooltip label={previewLabel} openDelay={400}>
          <ActionIcon
            variant="subtle"
            size="md"
            aria-label={previewLabel}
            aria-haspopup="dialog"
            onClick={() => setOpen(true)}
          >
            <IconEye size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      <Tooltip label={downloadLabel} openDelay={400}>
        {download ? (
          <ActionIcon variant="subtle" size="md" aria-label={downloadLabel} onClick={download}>
            <IconDownload size={18} />
          </ActionIcon>
        ) : (
          <ActionIcon
            component="a"
            href={url}
            download={fileName}
            variant="subtle"
            size="md"
            aria-label={downloadLabel}
          >
            <IconDownload size={18} />
          </ActionIcon>
        )}
      </Tooltip>
      {error ? (
        <span className={classes.error} role="alert">
          {t(`attachmentPreview.errors.${error}`, {
            defaultValue: t('attachmentPreview.errors.SAVE_FAILED'),
          })}
        </span>
      ) : null}
      {/* Kapalıyken de bağlı: kapanışta odak simgeye döner; dosya kapanınca bırakılır */}
      <AttachmentPreview
        opened={open}
        file={{ fileName, url }}
        onClose={() => setOpen(false)}
        onDownload={download}
        downloadHref={download ? undefined : url}
      />
    </span>
  );
}
